/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useEffect, useMemo, useState } from 'react';
import { Field, useForm, useFormState } from 'react-final-form';
import {
    CheckboxToggle, RenderIf, Slider, Tab, Textarea, Option,
} from 'react-rainbow-components';
import ReactJson from 'react-json-view';
import { JsonInput, UniversalPickerOption } from '@rainbow-modules/forms';
import OpenAI from 'components/icons/openai';
import RadioItem from 'components/Instructions/aiModelConfigForm/radioItem';
import isBuiltinModel from 'components/Instructions/helpers/isBuiltinModel';
import {
    FieldTitle, FieldDescription, CreativityHelpContainer,
    StyledPickerOptionContent, StyledOptionLabel, StyledUniversalPicker,
    StyledInput, StyledPicklist,
} from 'components/Instructions/aiModelConfigForm/styled';
import tryParseJson from 'data/services/string/parseJson';
import Input from 'components/Input';
import useGptModels from 'data/hooks/useGptModels';
import validateJsonSchema from './helpers/validateJsonSchema';
import {
    JSONValueContainer,
    SchemaInputContainer, StyledTabHeaderButton, StyledTabset, TabContentContainer,
} from './styled';
import TabHeader from './tabHeader';
import GPT4ModelPicker from '../../../components/GPT4ModelPicker';
import validateTools from './helpers/validateTools';
import ToolPicker from './ToolPicker';
import MemoryRetrieverSelector from './MemoryRetrieverSelector';

const defaultOutputSchemaValue = `{
    "type": "object",
    "properties": {
        "content": { "type": "string" }
    },
    "required": ["content"]
}`;

const defaultInputSchemaValue = `{
    "type": "object",
    "properties": {}
}`;

const DEFAULT_GPT_MODEL = 'gpt-4o-2024-05-13';

const isBuiltinGptModel = (modelId: string) => isBuiltinModel(modelId) && modelId?.startsWith('gpt-');

const ModelConfigFields = () => {
    const { change } = useForm();
    const { values, initialValues } = useFormState();
    const {
        model,
        modelId,
        jsonMode,
        tools = [],
    } = values;
    const canEditSchemas = useMemo(
        () => Boolean(!initialValues.featureSchema),
        [initialValues.featureSchema],
    );
    const [activeTab, setActiveTab] = useState('llm');
    const { data: availableModels } = useGptModels(true);
    const capabilities = availableModels?.data.find((item) => item.id === modelId)?.capabilities;
    const effort = values.generation?.reasoningEffort || capabilities?.defaultReasoningEffort;
    const ignoresTemperature = capabilities?.temperature === 'never'
        || (capabilities?.temperature === 'none-only' && effort !== 'none');

    const selectedEffort = values.generation?.reasoningEffort;
    const hasEffortOptions = Boolean(capabilities?.reasoningEfforts.length || selectedEffort);
    const invalidEffort = selectedEffort
        && !capabilities?.reasoningEfforts.includes(selectedEffort);
    const reasoningOptions = [
        {
            value: '',
            label: capabilities?.defaultReasoningEffort
                ? `Model default (${capabilities.defaultReasoningEffort})` : 'Model default',
        },
        ...(invalidEffort ? [{ value: selectedEffort, label: `${selectedEffort} (unsupported)` }] : []),
        ...(capabilities?.reasoningEfforts || []).map((value) => ({ value, label: value })),
    ];

    useEffect(() => {
        // TODO: final-form accepts a `destroyOnUnregister` prop that can be used to
        // remove field data automatically when the field is unregistered.
        // But our UniversalForm does not support this prop yet.
        if (
            !['fine-tuned', 'openai'].includes(model)
            || (model === 'fine-tuned' && isBuiltinGptModel(modelId))
        ) {
            change('modelId', undefined);
        }
        if (model === 'openai' && !isBuiltinGptModel(modelId)) {
            change('modelId', DEFAULT_GPT_MODEL);
        }
    }, [change, model, modelId]);

    useEffect(() => {
        if (canEditSchemas) {
            if (!jsonMode) {
                change('outputSchema', defaultOutputSchemaValue);
            } else {
                change('outputSchema', initialValues.outputSchema || JSON.parse(defaultOutputSchemaValue));
            }
        }
    }, [change, canEditSchemas, initialValues.outputSchema, jsonMode]);

    const handleResetInputSchema = () => change('inputSchema', defaultInputSchemaValue);

    return (
        <>
            <StyledTabset
                variant="line"
                activeTabName={activeTab}
                onSelect={(e, selectedTab) => setActiveTab(selectedTab)}
            >
                <Tab name="llm" label="LLM" />
                <Tab name="input" label="Input Schema" />
                <Tab name="output" label="Output Schema" />
                <Tab name="tools" label="Tools" />
                <Tab name="memory" label="Memory" />
                <Tab name="general" label="Info" />
            </StyledTabset>
            <TabContentContainer>
                <RenderIf isTrue={activeTab === 'llm'}>
                    <FieldTitle>Select Model</FieldTitle>
                    <FieldDescription>
                        Select an AI model tailored to your needs,
                        ranging from advanced and complex to simple
                        and direct for your assistant&apos;s interactions.
                    </FieldDescription>
                    <StyledUniversalPicker
                        name="model"
                        value={model}
                        onChange={(value: any) => change('model', value)}
                        direction="vertical"
                    >
                        <UniversalPickerOption component={RadioItem} name="openai">
                            <StyledPickerOptionContent>
                                <OpenAI />
                                <StyledOptionLabel>GPT</StyledOptionLabel>
                            </StyledPickerOptionContent>
                        </UniversalPickerOption>
                        <UniversalPickerOption component={RadioItem} name="fine-tuned">
                            <StyledPickerOptionContent>
                                <OpenAI />
                                <StyledOptionLabel>Fine Tuned</StyledOptionLabel>
                            </StyledPickerOptionContent>
                        </UniversalPickerOption>
                    </StyledUniversalPicker>
                    <RenderIf isTrue={model === 'fine-tuned'}>
                        <Field
                            name="modelId"
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            component={StyledInput}
                            label="Model ID"
                            labelAlignment="left"
                            borderRadius="semi-square"
                            validate={(value) => {
                                if (!value) {
                                    return 'Model ID is required for fine-tuned models';
                                }
                                if (!value.startsWith('ft:')) {
                                    return 'Fine-tuned model ID must start with "ft:"';
                                }
                                return undefined;
                            }}
                            required
                        />
                    </RenderIf>
                    <RenderIf isTrue={model === 'openai'}>
                        <Field
                            name="modelId"
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            component={GPT4ModelPicker}
                            responders
                            label="Select model"
                            labelAlignment="left"
                            borderRadius="semi-square"
                            required
                        />
                    </RenderIf>
                </RenderIf>
                <RenderIf isTrue={activeTab === 'llm'}>
                    {hasEffortOptions && (
                        <Field
                            name="generation.reasoningEffort"
                            parse={(value) => value || undefined}
                            validate={(value) => (capabilities && value
                                && !capabilities.reasoningEfforts.includes(value)
                                ? 'Choose a reasoning effort supported by this model' : undefined)}
                        >
                            {({ input, meta }) => {
                                const selectedOption = reasoningOptions.find(
                                    (option) => option.value === input.value,
                                ) || reasoningOptions[0];
                                return (
                                    <StyledPicklist
                                        name={input.name}
                                        label="Reasoning effort"
                                        labelAlignment="left"
                                        value={{
                                            ...selectedOption,
                                            name: selectedOption.value || 'model-default',
                                        }}
                                        onChange={(option) => input.onChange(option.value)}
                                        onFocus={() => input.onFocus()}
                                        onBlur={() => input.onBlur()}
                                        error={(meta.touched || meta.submitFailed)
                                            ? meta.error : undefined}
                                    >
                                        {reasoningOptions.map((option) => (
                                            <Option
                                                key={option.value || 'model-default'}
                                                name={option.value || 'model-default'}
                                                label={option.label}
                                                value={option.value}
                                            />
                                        ))}
                                    </StyledPicklist>
                                );
                            }}
                        </Field>
                    )}
                    {capabilities?.status === 'unverified' && (
                        <FieldDescription>
                            Compatibility is unverified. Test this configuration before saving.
                        </FieldDescription>
                    )}
                    <br />
                    <FieldTitle>Creativity</FieldTitle>
                    <FieldDescription>
                        Increase the creativity setting for imaginative content or
                        reduce it for more straightforward tasks.
                    </FieldDescription>
                    <Field
                        name="temperature"
                        disabled={ignoresTemperature}
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        component={Slider}
                        type="number"
                        min={0}
                        max={2}
                        step={0.01}
                    />
                    <CreativityHelpContainer>
                        <span>consistent</span>
                        <span>creative</span>
                    </CreativityHelpContainer>
                    {ignoresTemperature && (
                        <FieldDescription>
                            Creativity is inactive for this model and reasoning setting.
                            Its saved value is preserved.
                        </FieldDescription>
                    )}
                </RenderIf>
                <RenderIf isTrue={activeTab === 'input'}>
                    <SchemaInputContainer>
                        <TabHeader
                            title="Input Schema"
                            description="Define the the input for this responder. The variables can be used on the instructions."
                            actions={(
                                // eslint-disable-next-line max-len
                                <RenderIf isTrue={values.inputSchema !== defaultInputSchemaValue && !values.featureSchema}>
                                    <StyledTabHeaderButton
                                        label="Use Default Schema"
                                        variant="base"
                                        size="small"
                                        borderRadius="semi-square"
                                        onClick={handleResetInputSchema}
                                    />
                                </RenderIf>
                            )}
                        />
                        <RenderIf isTrue={!values.featureSchema}>
                            <Field
                                name="inputSchema"
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                // @ts-ignore
                                component={JsonInput}
                                validate={validateJsonSchema}
                                defaultValue={defaultInputSchemaValue}
                                disabled={Boolean(values.featureSchema)}
                            />
                        </RenderIf>
                        <RenderIf isTrue={Boolean(values.featureSchema)}>
                            <JSONValueContainer>
                                <ReactJson
                                    src={values.inputSchema}
                                    displayObjectSize={false}
                                    enableClipboard={false}
                                    displayDataTypes={false}
                                />
                            </JSONValueContainer>
                        </RenderIf>
                    </SchemaInputContainer>
                </RenderIf>
                <RenderIf isTrue={activeTab === 'output'}>
                    <SchemaInputContainer>
                        <TabHeader
                            title="Output Schema"
                            description="Define the output for this responder, it can be a text, a json..."
                            actions={(
                                <RenderIf isTrue={canEditSchemas}>
                                    <Field
                                        name="jsonMode"
                                        label="JSON Mode"
                                        labelAlignment="left"
                                        // @ts-ignore
                                        component={CheckboxToggle}
                                        disabled={Boolean(values.featureSchema)}
                                    />
                                </RenderIf>
                            )}
                        />
                        <RenderIf isTrue={canEditSchemas && jsonMode}>
                            <Field
                                name="outputSchema"
                                // @ts-ignore
                                component={JsonInput}
                                labelAlignment="left"
                                defaultValue={defaultOutputSchemaValue}
                                validate={validateJsonSchema}
                            />
                        </RenderIf>
                        <RenderIf isTrue={!canEditSchemas}>
                            <JSONValueContainer>
                                <ReactJson
                                    src={
                                        values.outputSchema
                                        || tryParseJson(defaultOutputSchemaValue)
                                    }
                                    displayObjectSize={false}
                                    enableClipboard={false}
                                    displayDataTypes={false}
                                />
                            </JSONValueContainer>
                        </RenderIf>
                    </SchemaInputContainer>
                </RenderIf>
                <RenderIf isTrue={activeTab === 'tools'}>
                    <SchemaInputContainer>
                        <TabHeader
                            title="All Tools"
                            description="Add a descriptive text here"
                            actions={(
                                <Field
                                    name="toolsChoice"
                                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                    // @ts-ignore
                                    component={ToolPicker}
                                    tools={tools}
                                />
                            )}
                        />
                        <Field
                            name="tools"
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            component={JsonInput}
                            validate={validateTools}
                        />
                    </SchemaInputContainer>
                </RenderIf>
                <RenderIf isTrue={activeTab === 'memory'}>
                    <MemoryRetrieverSelector
                        memoryRetrieverIds={values.memoryRetrieverIds || []}
                        onChange={(value) => change('memoryRetrieverIds', value)}
                    />
                </RenderIf>
                <RenderIf isTrue={activeTab === 'general'}>
                    <Field
                        component={Input}
                        name="name"
                        label="Name"
                        labelAlignment="left"
                        placeholder="Enter a name for the AI Persona"
                        borderRadius="semi-rounded"
                        required
                        className="rainbow-m-bottom_medium"
                    />
                    <Field
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        component={Textarea}
                        name="description"
                        label="Description"
                        labelAlignment="left"
                        borderRadius="semi-rounded"
                        placeholder="Enter a description for the AI Persona"
                        className="rainbow-m-bottom_medium"
                    />
                </RenderIf>
            </TabContentContainer>
        </>
    );
};

export default ModelConfigFields;
